import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import SeoHead from "../components/seo/SeoHead";
import Footer from "../components/Footer";

const THEME_KEY = "hookora_theme";

function RefundPolicyPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#4b5563",
      badgeBg: isDark ? "rgba(29,78,216,0.18)" : "#eff6ff",
      badgeText: isDark ? "#93c5fd" : "#1d4ed8",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 10px 30px rgba(15, 23, 42, 0.05)"
    }),
    [isDark]
  );

  const sectionTitleStyle = {
    color: colors.text,
    marginBottom: "10px"
  };

  const paragraphStyle = {
    color: colors.textSoft,
    lineHeight: "1.8"
  };

  const listStyle = {
    color: colors.textSoft,
    lineHeight: "1.9",
    paddingLeft: "20px"
  };

  return (
    <div style={{ background: colors.pageBg, backgroundImage: colors.pageGradient, minHeight: "100vh" }}>
      <SeoHead
        title="Refund & Cancellation Policy | Hookora"
        description="Read Hookora's refund policy and cancellation terms for subscriptions, duplicate payments, and billing support."
        path="/refund-policy"
      />

      <Navbar />

      <div
        style={{
          maxWidth: "900px",
          margin: "0 auto",
          padding: "32px 16px 56px"
        }}
      >
        <div
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "24px",
            padding: "28px",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none"
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              borderRadius: "999px",
              background: colors.badgeBg,
              color: colors.badgeText,
              fontSize: "12px",
              fontWeight: "700",
              marginBottom: "14px",
              border: isDark ? `1px solid ${colors.border}` : "none"
            }}
          >
            Billing Policy
          </div>

          <h1
            style={{
              marginTop: 0,
              marginBottom: "10px",
              color: colors.text,
              fontSize: "34px",
              lineHeight: "1.1"
            }}
          >
            Refund & Cancellation Policy
          </h1>

          <p
            style={{
              color: colors.textMuted,
              lineHeight: "1.7",
              marginBottom: "28px",
              fontSize: "15px"
            }}
          >
            Hookora is committed to keeping billing clear, fair, and transparent.
            This page explains when payments are refundable, how cancellations
            work, and when duplicate payments may qualify for review.
          </p>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Refund Policy
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              When a user subscribes to a paid Hookora plan, access to that plan
              begins once payment is successfully completed. Because plan access
              is granted immediately after activation, refunds are generally limited.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h3 style={sectionTitleStyle}>
              Non-refundable payments
            </h3>
            <p style={{ ...paragraphStyle, marginTop: 0 }}>
              Payments are not refundable in the following situations:
            </p>
            <ul style={listStyle}>
              <li>a user cancels after the plan has already become active</li>
              <li>a user changes their mind after successful payment</li>
              <li>a user does not fully use the service after subscribing</li>
              <li>a user forgets to cancel before the next billing cycle</li>
            </ul>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>
              Once a paid plan is active, the payment for that billing period is
              considered used for access to the service.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h3 style={sectionTitleStyle}>
              Refunds for duplicate or mistaken payments
            </h3>
            <p style={{ ...paragraphStyle, marginTop: 0 }}>
              A refund may be considered only in limited cases where a payment
              appears to have been made by mistake.
            </p>
            <ul style={listStyle}>
              <li>more than one payment made in quick succession for the same plan</li>
              <li>a duplicate charge caused by a payment issue</li>
              <li>
                an unintended repeated payment that may have resulted from a network
                failure or billing error
              </li>
            </ul>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>
              Refunds under these circumstances are not automatic and will be
              reviewed before approval.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h3 style={sectionTitleStyle}>
              Refund review
            </h3>
            <p style={{ ...paragraphStyle, marginTop: 0 }}>
              To request a refund review, the user must contact Hookora support and provide:
            </p>
            <ul style={listStyle}>
              <li>the email address connected to the account</li>
              <li>the payment reference or transaction proof</li>
              <li>a short explanation of what happened</li>
            </ul>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>
              Hookora reserves the right to review all billing activity and determine
              whether a refund request qualifies under this policy.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Cancellation Terms
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              Users may cancel their subscription at any time. Cancellation prevents
              future renewals, but it does not refund the current billing period once
              the subscription is already active.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h3 style={sectionTitleStyle}>
              No refund after activation
            </h3>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              If a user cancels an already active plan, no refund will be issued
              for that active billing period. The user will continue to have access
              to the paid plan until the end of the current subscription period,
              after which the subscription will not renew unless reactivated.
            </p>
          </section>

          <section>
            <h3 style={sectionTitleStyle}>
              Duplicate payment exception
            </h3>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              If a user is charged more than once in quick succession for the same
              plan, and the repeated charge appears to be accidental or caused by a
              payment or network issue, Hookora may review the case and issue a
              refund where appropriate. This exception applies only to clearly
              duplicate or mistaken charges, not to ordinary subscription cancellations.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default RefundPolicyPage;